/**
 * API & Client Contracts
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralizes all client-side configuration and contracts used when
 * interacting with backend APIs.
 *
 * This file defines:
 * - API request configuration (base URL, timeouts, headers)
 * - Stable error codes used across the frontend
 * - Translation key references for error messages and titles
 * - Mappings from HTTP status codes to domain error codes
 * - Local storage keys related to authentication
 *
 * Design Principles:
 * ------------------
 * - Constants in this file are treated as PUBLIC CONTRACTS.
 * - Values should be stable and backward-compatible.
 * - UI components must not hardcode error strings or status codes.
 * - Error messages use translation keys, not hardcoded strings.
 *
 * Non-goals:
 * ----------
 * - Business logic
 * - API call implementations
 * - UI-specific behavior
 * - Performing actual translations (handled by utils)
 *
 * Any changes here affect error handling, networking, and user experience
 * across the entire application.
 * ============================================================================
 */

export const UPLOAD_TIMEOUT = 300000; // 5 minutes
export const TIMEOUT = 30000; // 30 seconds

/**
 * API configuration constants
 *
 * Centralized configuration for all API requests.
 * Environment variables override defaults.
 */
export const API_CONFIG = {
  baseURL:
    (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? 'http://10.29.243.174:8000/api/v1',
  timeout: TIMEOUT,
  uploadTimeout: UPLOAD_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
} as const;

/**
 * Local storage keys for authentication
 *
 * Prefixed with 'bimanova_' to avoid conflicts with other apps.
 * NOTE: These must match AUTH_STORAGE_KEYS in core/auth/constants.ts
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'bimanova_access_token',
  REFRESH_TOKEN: 'bimanova_refresh_token',
  USER_DATA: 'bimanova_user_data',
} as const;

/**
 * Error codes returned by API
 *
 * Mapped from HTTP status codes and backend error responses.
 */
export const ERROR_CODES = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CANCELED: 'CANCELED',
  BAD_REQUEST: 'BAD_REQUEST',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  CONFLICT: 'CONFLICT',
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

/**
 * Translation keys for error messages
 *
 * Maps error codes to i18n translation keys.
 * Actual translation happens in utils using i18n.t().
 * Backend messages override these when available.
 */
export const ERROR_MESSAGE_KEYS: Record<(typeof ERROR_CODES)[keyof typeof ERROR_CODES], string> = {
  [ERROR_CODES.NETWORK_ERROR]: 'api-errors:messages.networkError',
  [ERROR_CODES.TIMEOUT]: 'api-errors:messages.timeout',
  [ERROR_CODES.CANCELED]: 'api-errors:messages.canceled',
  [ERROR_CODES.BAD_REQUEST]: 'api-errors:messages.badRequest',
  [ERROR_CODES.UNAUTHORIZED]: 'api-errors:messages.unauthorized',
  [ERROR_CODES.FORBIDDEN]: 'api-errors:messages.forbidden',
  [ERROR_CODES.NOT_FOUND]: 'api-errors:messages.notFound',
  [ERROR_CODES.VALIDATION_ERROR]: 'api-errors:messages.validationError',
  [ERROR_CODES.CONFLICT]: 'api-errors:messages.conflict',
  [ERROR_CODES.INTERNAL_SERVER_ERROR]: 'api-errors:messages.internalServerError',
  [ERROR_CODES.SERVICE_UNAVAILABLE]: 'api-errors:messages.serviceUnavailable',
  [ERROR_CODES.UNKNOWN_ERROR]: 'api-errors:messages.unknownError',
};

/**
 * Translation keys for error titles
 *
 * Maps error codes to i18n translation keys for notification titles.
 */
export const TITLE_KEYS: Record<string, string> = {
  NETWORK_ERROR: 'api-errors:titles.networkError',
  TIMEOUT: 'api-errors:titles.timeout',
  VALIDATION_ERROR: 'api-errors:titles.validationError',
  UNAUTHORIZED: 'api-errors:titles.unauthorized',
  FORBIDDEN: 'api-errors:titles.forbidden',
  NOT_FOUND: 'api-errors:titles.notFound',
  INTERNAL_SERVER_ERROR: 'api-errors:titles.internalServerError',
};

/** Map HTTP status codes to error codes
 *
 * Used when backend does not provide specific error codes.
 */
export const ERROR_CODE_MAP: Record<number, string> = {
  400: 'BAD_REQUEST',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  422: 'VALIDATION_ERROR',
  500: 'INTERNAL_SERVER_ERROR',
  503: 'SERVICE_UNAVAILABLE',
};
